/**
 * Room-clear progress, persisted to localStorage (brief §14 item 7).
 *
 * Just a set of cleared room ids. There is no Moonseed Tree or part-unlock
 * system in code yet (brief §14 item 4 remains partly unbuilt - see the
 * Grove map's own note), so this tracks exactly one thing: which portals are
 * lit on the map.
 */
const STORAGE_KEY = 'moonseed:cleared';
const ASSIST_STORAGE_KEY = 'moonseed:assist-mode';
const CHARACTER_STORAGE_KEY = 'moonseed:selected-character';

/** localStorage can throw (private browsing, disabled storage, quota) - every
 *  call here is a convenience, never something the game depends on to run. */
export function loadCleared(): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

export function markCleared(id: string): ReadonlySet<string> {
  const next = new Set(loadCleared());
  next.add(id);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  } catch {
    // Progress just won't persist this session; the room is still cleared in memory.
  }
  return next;
}

/** Assist Mode is a player preference, separate from room progress. */
export function loadAssistMode(): boolean {
  try {
    return window.localStorage.getItem(ASSIST_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function saveAssistMode(enabled: boolean): void {
  try {
    window.localStorage.setItem(ASSIST_STORAGE_KEY, String(enabled));
  } catch {
    // The setting remains active for this visit even when storage is disabled.
  }
}

export function loadSelectedCharacter(): string {
  try {
    return window.localStorage.getItem(CHARACTER_STORAGE_KEY) ?? 'kotaro';
  } catch {
    return 'kotaro';
  }
}

export function saveSelectedCharacter(id: string): void {
  try {
    window.localStorage.setItem(CHARACTER_STORAGE_KEY, id);
  } catch {
    // Selection remains active for this visit if storage is unavailable.
  }
}
